import { FC, memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import { zhCN, enUS, ja } from "date-fns/locale";
import { cn } from "@/lib/utils";
import {
  Bell,
  Gift,
  Trophy,
  FileText,
  AlertCircle,
  Check,
  MoreHorizontal,
  Trash2,
  Clock,
  CheckCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { UserNotification } from "@/services/notificationService";
import { NotificationBadge } from "./NotificationBadge";

const NOTIFICATION_ICONS = {
  reward: Gift,
  lottery_win: Trophy,
  lottery_result: Trophy,
  new_article: FileText,
  article: FileText,
  system: Bell,
  system_message: Bell,
  general: Bell,
  alert: AlertCircle,
  warning: AlertCircle,
} as const;

const LOCALES: Record<string, any> = {
  zh: zhCN,
  "zh-CN": zhCN,
  en: enUS,
  ja: ja,
};

function formatRelativeTime(dateString: string, language: string): string {
  const date = new Date(dateString);
  const locale = LOCALES[language] || zhCN;

  return formatDistanceToNow(date, {
    addSuffix: true,
    locale: locale,
  });
}

interface NotificationCardProps {
  notification: UserNotification;
  onMarkAsRead?: (id: number) => void;
  onDelete?: (id: number) => void;
  isDeleting?: boolean;
  isMarkingRead?: boolean;
}

export const NotificationCard: FC<NotificationCardProps> = memo(
  ({
    notification,
    onMarkAsRead,
    onDelete,
    isDeleting = false,
    isMarkingRead = false,
  }) => {
    const { t, i18n } = useTranslation("common");
    const isRead = !!notification.read_at;
    const Icon =
      NOTIFICATION_ICONS[
        notification.notification_type as keyof typeof NOTIFICATION_ICONS
      ] || Bell;
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    return (
      <>
        <motion.div
          layout
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          whileHover={{ scale: 1.005 }}
          transition={{ duration: 0.2 }}
          className={cn(
            "group relative overflow-hidden rounded-2xl border p-4 transition-all duration-300",
            isRead
              ? "bg-background/40 border-border/40 text-muted-foreground hover:bg-muted/30"
              : "bg-card border-border shadow-sm hover:shadow-md hover:border-primary/20",
            isDeleting && "opacity-50 pointer-events-none",
          )}
        >
          {/* Unread Indicator */}
          {!isRead && (
            <div className="absolute top-4 right-4 h-2.5 w-2.5 rounded-full bg-primary shadow-sm ring-4 ring-primary/10" />
          )}

          <div className="flex gap-4">
            {/* Icon Section */}
            <div className="flex-shrink-0">
              <div
                className={cn(
                  "flex h-12 w-12 items-center justify-center rounded-2xl transition-colors",
                  notification.important
                    ? "bg-destructive/10 text-destructive"
                    : isRead
                      ? "bg-muted text-muted-foreground"
                      : "bg-primary/10 text-primary",
                )}
              >
                <Icon className="h-5 w-5" />
              </div>
            </div>

            {/* Content Section */}
            <div className="flex-1 min-w-0 py-0.5">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 pr-6">
                  <div className="flex items-center gap-2">
                    <h3
                      className={cn(
                        "font-medium leading-none tracking-tight truncate",
                        isRead ? "text-muted-foreground" : "text-foreground",
                      )}
                    >
                      {notification.title}
                    </h3>
                    <NotificationBadge
                      important={notification.important}
                      broadcast={notification.broadcast}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                    {notification.message}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <div className="flex items-center gap-3 text-xs text-muted-foreground/60 font-medium">
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {formatRelativeTime(notification.created_at, i18n.language)}
                  </span>
                </div>

                {/* Actions - visible on hover or if important actions needed */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity translate-x-4 group-hover:translate-x-0">
                  {!isRead && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full hover:bg-primary/10 hover:text-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkAsRead?.(notification.id);
                      }}
                      disabled={isMarkingRead}
                      title={t("notifications.actions.markRead")}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full hover:bg-muted"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {isRead && (
                        <DropdownMenuItem
                          onClick={() => onMarkAsRead?.(notification.id)}
                        >
                          <CheckCheck className="h-4 w-4 mr-2" />
                          标记为未读
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteDialog(true);
                        }}
                        className="text-destructive focus:text-destructive focus:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        删除
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>删除通知</AlertDialogTitle>
              <AlertDialogDescription>
                确定要删除这条通知吗？此操作无法撤销。
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>取消</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  onDelete?.(notification.id);
                  setShowDeleteDialog(false);
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                删除
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  },
);
